import React, { useState, useEffect } from 'react';
import { Star, StarHalf } from 'lucide-react';

/**
 * StarRating Component
 * Displays star rating for a listing with optional review count
 * 
 * @param {string} listingId - The listing ID to fetch rating for
 * @param {number} rating - Direct rating value (if not fetching)
 * @param {number} reviewCount - Direct review count (if not fetching)
 * @param {string} size - Size variant: 'sm', 'md', 'lg'
 * @param {boolean} showCount - Whether to show review count
 * @param {boolean} fetchData - Whether to fetch data from API
 */
const StarRating = ({ 
  listingId, 
  rating: propRating, 
  reviewCount: propReviewCount,
  size = 'sm',
  showCount = true,
  fetchData = false
}) => {
  const [rating, setRating] = useState(propRating || 0);
  const [reviewCount, setReviewCount] = useState(propReviewCount || 0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (fetchData && listingId) {
      fetchRating();
    }
  }, [listingId, fetchData]);

  useEffect(() => {
    if (propRating !== undefined) setRating(propRating);
    if (propReviewCount !== undefined) setReviewCount(propReviewCount);
  }, [propRating, propReviewCount]);

  const fetchRating = async () => {
    setLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/reviews/stats/listing/${listingId}`);
      if (response.ok) {
        const data = await response.json();
        setRating(data.average_rating || 0);
        setReviewCount(data.total_reviews || 0);
      }
    } catch (error) {
      // Silent fail - rating display is not critical
    } finally {
      setLoading(false);
    }
  };

  const sizeClasses = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  };

  const textSizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base'
  };

  const starSize = sizeClasses[size] || sizeClasses.sm;
  const textSize = textSizeClasses[size] || textSizeClasses.sm;

  const renderStars = () => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Star key={i} className={`${starSize} fill-yellow-400 text-yellow-400`} />
      );
    }
    if (hasHalfStar && fullStars < 5) {
      stars.push(
        <StarHalf key="half" className={`${starSize} fill-yellow-400 text-yellow-400`} />
      );
    }
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <Star key={`empty-${i}`} className={`${starSize} text-gray-300`} />
      );
    }
    return stars;
  };

  if (loading) {
    return (
      <div className="flex items-center gap-1 animate-pulse">
        {[...Array(5)].map((_, i) => (
          <div key={`placeholder-${i}`} className={`${starSize} bg-gray-200 rounded`} />
        ))}
      </div>
    );
  }

  if (reviewCount === 0 && !propRating) {
    // Show empty stars for products without reviews
    return (
      <div className="flex items-center gap-1">
        <div className="flex">
          {[...Array(5)].map((_, i) => (
            <Star key={`empty-star-${i}`} className={`${sizeClasses[size] || sizeClasses.sm} text-gray-300`} />
          ))}
        </div>
        {showCount && (
          <span className={`${textSizeClasses[size] || textSizeClasses.sm} text-gray-400 ml-1`}>
            (0)
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <div className="flex">{renderStars()}</div>
      {showCount && (
        <span className={`${textSize} text-gray-500 ml-1`}>
          {rating > 0 && <span className="font-medium text-gray-700">{rating.toFixed(1)}</span>}
          {reviewCount > 0 && <span> ({reviewCount})</span>}
        </span>
      )}
    </div>
  );
};

export default StarRating;
